import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import { useConfirm } from 'sutra/components/confirm/use-confirm'

import { RegionSelectionOverlay } from 'features/bug-report/components/RegionSelectionOverlay'
import { ReportBugButton } from 'features/bug-report/components/ReportBugButton'
import { ReportBugModal } from 'features/bug-report/components/ReportBugModal'
import { useBugReport } from 'features/bug-report/hooks/use-bug-report'
import { useBugReportForm } from 'features/bug-report/hooks/use-bug-report-form'
import { useBugReportUIStore } from 'features/bug-report/store'
import type { SelectedRegion } from 'features/bug-report/types'

type Props = {
  projectId?: string
}

const ReportBug: React.FC<Props> = ({ projectId }) => {
  const { state, actions } = useBugReport({ projectId })
  const isOpenRequested = useBugReportUIStore((s) => s.isOpenRequested)
  const clearOpenRequest = useBugReportUIStore((s) => s.clearOpenRequest)

  // Open requests from outside the feature (e.g. the command palette) run
  // the full openModal flow so tracking and the auto-screenshot still happen.
  useEffect(() => {
    if (!isOpenRequested) return
    clearOpenRequest()
    void actions.openModal()
  }, [isOpenRequested, clearOpenRequest, actions])
  const { formik, errorMessage } = useBugReportForm({
    onSubmit: async (message, { resetForm }) => {
      const { ok } = await actions.submit(message)
      if (ok) {
        toast.success('Bug report sent. Thanks!')
        resetForm()
        actions.closeModal()
      } else {
        toast.error('Failed to send bug report.')
      }
    },
  })

  const confirm = useConfirm()

  const discard = (): void => {
    actions.closeModal()
    formik.resetForm()
  }

  const hasUnsavedData =
    formik.values.message.trim().length > 0 ||
    state.visibleAttachments.length > 0

  const handleCancel = (): void => {
    if (!hasUnsavedData) {
      discard()
      return
    }
    void confirm({
      title: 'Discard your bug report?',
      content: 'Your message and any attachments will be lost.',
      okText: 'Discard',
      danger: true,
      cancelText: 'Keep editing',
      onOk: discard,
    })
  }

  const handleUploadFiles = async (files: File[]): Promise<void> => {
    const { errors } = await actions.addUploads(files)
    errors.forEach((message) => toast.error(message))
  }

  const handleRegionSelected = async (
    region: SelectedRegion,
  ): Promise<void> => {
    const ok = await actions.completeRegionSelect(region)
    if (!ok) toast.error('Could not capture that region.')
  }

  return (
    <>
      <ReportBugButton onClick={actions.openModal} />
      <ReportBugModal
        open={state.isOpen}
        message={formik.values.message}
        onMessageChange={formik.handleChange}
        onMessageBlur={formik.handleBlur}
        errorMessage={errorMessage}
        isSubmitting={formik.isSubmitting}
        attachments={state.visibleAttachments}
        onSubmit={formik.handleSubmit}
        onCancel={handleCancel}
        onTakeScreenshot={actions.startRegionSelect}
        onUploadFiles={handleUploadFiles}
        onRemoveAttachment={actions.removeAttachment}
      />
      {state.shouldShowOverlay && (
        <RegionSelectionOverlay
          onSelect={handleRegionSelected}
          onCancel={actions.cancelRegionSelect}
        />
      )}
    </>
  )
}

export default ReportBug
